import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { ShieldAlert } from 'lucide-react';
import { formatCurrency } from '../utils/formatCurrency';
import { useRestrictedWalletCredit } from '../hooks/useRestrictedWalletCredit';

const RESTRICTED_MODAL_DESC_ID = 'restricted-wallet-credit-description';

interface RestrictedWalletCreditModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional error message from a blocked spend attempt (overrides banner copy). */
  blockedMessage?: string;
}

/**
 * Explainer modal shown when a user has a restricted wallet balance
 * (CP disbursement credited before KYC) and tries to spend, or proactively
 * on the dashboard. Explains the funds are held until KYC Tier 1 and offers
 * a CTA to the KYC flow.
 */
export const RestrictedWalletCreditModal: React.FC<RestrictedWalletCreditModalProps> = ({
  isOpen,
  onClose,
  blockedMessage,
}) => {
  const navigate = useNavigate();
  const { balance } = useRestrictedWalletCredit();

  useEffect(() => {
    if (!isOpen) return;
    const raf = requestAnimationFrame(() => {
      const overlays = document.querySelectorAll('[data-slot="dialog-overlay"]');
      const lastOverlay = overlays[overlays.length - 1] as HTMLElement | undefined;
      if (lastOverlay) lastOverlay.style.zIndex = '10000';
      const contents = document.querySelectorAll('[data-slot="dialog-content"]');
      const lastContent = contents[contents.length - 1] as HTMLElement | undefined;
      if (lastContent) lastContent.style.zIndex = '10001';
    });
    return () => cancelAnimationFrame(raf);
  }, [isOpen]);

  const handleVerify = () => {
    onClose();
    navigate('/kyc/documents');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm mx-auto" aria-describedby={RESTRICTED_MODAL_DESC_ID}>
        <DialogDescription id={RESTRICTED_MODAL_DESC_ID} className="sr-only">
          You have funds waiting in your wallet. Complete ID verification to spend them.
        </DialogDescription>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-yellow-500" />
            Verify your ID to spend
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-gray-600 leading-relaxed">
            {blockedMessage ||
              `You have ${formatCurrency(balance)} waiting in your wallet. Verify your ID (KYC) to send money, buy airtime, or withdraw.`}
          </p>
          <p className="text-xs text-gray-500 mt-3 leading-relaxed">
            Your funds are held safely in your wallet until you verify your ID. Complete KYC to unlock sending, airtime, and withdrawals.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <Button
            onClick={handleVerify}
            className="bg-gradient-to-r from-[#86BE41] to-[#2D8CCA] text-white"
          >
            Verify my ID now
          </Button>
          <Button onClick={onClose} variant="outline">
            Later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
